import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL_USER_SERVICE } from '@/api/constant';
import CommonFilter from '@/components/common/CommonFilter';
import CommonTable, {
  Action,
  TableColumn,
} from '@/components/common/commonTable/CommonTable';
import ExportOption from '@/components/common/export/ExportOption';
import ConfirmationModel from '@/components/common/models/ConfrimationModel';
import PageHeading from '@/components/common/PageHeading';
import Pagination from '@/components/common/Pagination';
import StatusDropdown from '@/components/common/StatusComponent';
import Layout from '@/components/Layout/Layout';
import Card from '@/components/UI/Card';
import DateRangePicker from '@/components/UI/DateRange';
import { useListQueryParams } from '@/hooks/useListQueryParams';
import { useSelectableTable } from '@/hooks/useSelectableTable';
import {
  Customer,
  ICustomerResponse,
  ICustomerStates,
} from '@/interfaces/customer.interface';
import { UserRoles } from '@/utility/enum';
import {
  GetFilterUrl,
  showToastNotification,
  toFixedThree,
} from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { useEffect, useState } from 'react';
import { Range } from 'react-date-range';
import { useNavigate } from 'react-router-dom';

const Customers: React.FC = () => {
  const navigate = useNavigate();

  // ✅ URL-driven params
  const { params, setParams } = useListQueryParams({
    search: '',
    type: '',
    roleId: '',
  });

  const { page, limit, search, type, roleId } = params;

  // ✅ Local state (UI + API response)
  const [state, setState] = useState<ICustomerStates>({
    customer: [],
    pageInfo: {
      total: 0,
      totalPages: 0,
      limit,
      page,
    },
    isLoading: false,
    selectedUserId: null,
    showStatusModel: false,
    showVerificationModel: false,
    statusToUpdate: null,
  });

  const [dateRange, setDateRange] = useState<Range[]>([
    {
      startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      endDate: new Date(),
      key: 'selection',
    },
  ]);

  const { selectedItems, handleSelectAll, handleSelectItem } =
    useSelectableTable(state.customer);

  // 🔹 Fetch customers
  const fetchCustomers = async () => {
    try {
      setState((prev) => ({ ...prev, isLoading: true }));

      const query = GetFilterUrl({
        page,
        limit,
        fullName: search,
        type,
        roleId,
      });

      const PATH = `${API_URL_USER_SERVICE}${API_ENDPOINT.users}/list?${query}`;
      const res = await callApi<ICustomerResponse>(PATH, { method: 'GET' });

      setState((prev) => ({
        ...prev,
        customer: res.data,
        pageInfo: res.pageInfo,
        isLoading: false,
      }));
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, [page, limit, search, type, roleId, dateRange]);

  // 🔹 Verify user
  const handleVerifyUser = async (id: string, status: string) => {
    try {
      const PATH = `${API_URL_USER_SERVICE}${API_ENDPOINT.users}/verify-user/${id}`;
      await callApi(PATH, {
        method: 'PATCH',
        data: { isVerified: status === 'verified' },
      });
      fetchCustomers();
    } catch (error) {
      ThrowError(error);
    }
  };

  // 🔹 Block / unblock user
  const handleBlockUser = async (
    userId: string,
    status: 'blocked' | 'active',
  ) => {
    try {
      const PATH = `${API_URL_USER_SERVICE}${API_ENDPOINT.users}/block/${userId}?isBlocked=${status === 'blocked'}`;
      await callApi(PATH, { method: 'PATCH' });
      setParams({ page: 1 });
    } catch (error: any) {
      showToastNotification('danger', error.message);
    }
  };

  const filters = [
    { label: 'All', value: '' },
    { label: 'Customers', value: 'customer' },
    { label: 'Employee', value: 'employee' },
  ];

  const customerColumns: TableColumn<Customer>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (customer) => (
        <span
          className={customer?.isEmployee ? 'text-blue-500' : ''}
        >{`${customer?.fullName}`}</span>
      ),
      className: 'p-3 font-medium text-gray-900 ',
    },
    {
      key: 'type',
      header: 'Type',
      render: (customer) => {
        return (
          <>
            <StatusDropdown
              options={[
                {
                  label: 'Verified',
                  value: 'verified',
                  textClass: 'text-yellow-500',
                  bgClass: 'bg-yellow-500',
                },
                {
                  label: 'Unverified',
                  value: 'unVerified',
                  textClass: 'text-purple-500',
                  bgClass: 'bg-purple-500',
                },
              ]}
              canEditVal="user"
              value={customer?.isVerified ? 'verified' : 'unVerified'}
              onChange={(value) =>
                setState((prev) => ({
                  ...prev,
                  selectedUserId: customer.id,
                  statusToUpdate: value,
                  showVerificationModel: true,
                }))
              }
            />
          </>
        );
      },
    },
    {
      key: 'email',
      header: 'Email',
      render: (customer) => (
        <span className={customer?.isEmployee ? 'text-blue-500' : ''}>
          {customer?.email}
        </span>
      ),
    },
    {
      key: 'orders',
      header: 'Orders',
      render: (customer) => (
        <span className={customer?.isEmployee ? 'text-blue-500' : ''}>
          {customer?.orderDetails?.orders}
        </span>
      ),
    },
    {
      key: 'isBlocked',
      header: 'Status',
      render: (customer) => {
        return (
          <>
            <StatusDropdown
              options={
                customer.isEmployee
                  ? [
                      {
                        label: 'Active',
                        value: 'active',
                        textClass: 'text-green-500',
                        bgClass: 'bg-green-500',
                      },
                      {
                        label: 'Blocked',
                        value: 'blocked',
                        textClass: 'text-red-500',
                        bgClass: 'bg-red-500',
                      },
                    ]
                  : []
              }
              canEditVal="user"
              value={customer?.isBlocked ? 'blocked' : 'active'}
              onChange={(status) =>
                setState((prev) => ({
                  ...prev,
                  selectedUserId: customer.id,
                  showStatusModel: true,
                  statusToUpdate: status,
                }))
              }
            />
          </>
        );
      },
    },

    {
      key: 'amountSpent',
      header: 'Amount Spend (BHD)',
      render: (customer) => (
        <span className={customer?.isEmployee ? 'text-blue-500' : ''}>
          {toFixedThree(customer?.orderDetails?.spends)}
        </span>
      ),
    },
  ];

  const actions: Action<Customer>[] = [
    {
      text: 'View',
      onClick: (customer) =>
        navigate(`${RoutesLink.customerDetails}/${customer.id}`),
    },
  ];

  return (
    <Layout>
      <PageHeading text="Customer" />

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <CommonFilter
            activeFilter={type}
            filters={filters}
            inputPlaceHolder="Search for Customers..."
            searchValue={search}
            onSearch={(text) => setParams({ search: text })}
            onChange={(filter) =>
              setParams({ type: filter, roleId: '', page: 1 })
            }
          />

          <div className="flex items-center gap-3 flex-wrap">
            <DateRangePicker
              dateRange={dateRange}
              setDateRange={setDateRange}
            />
            <ExportOption
              APIPath={`${API_URL_USER_SERVICE}${API_ENDPOINT.users}/export`}
              dateRange={dateRange[0]}
              isEmployee={type === UserRoles.EMPLOYEE}
              isStaff={type === UserRoles.STAFF}
            />
          </div>
        </div>

        <CommonTable
          columns={customerColumns}
          data={state.customer}
          selectedItems={selectedItems}
          onSelectAll={handleSelectAll}
          onSelectItem={handleSelectItem}
          getItemId={(item) => item.id}
          emptyMessage="No Customers found"
          loading={state.isLoading}
          actions={actions}
        />

        {state.pageInfo.totalPages > 1 && (
          <Pagination
            paginationData={state.pageInfo}
            onPageChange={(p) => setParams({ page: p })}
            onPageSizeChange={(l) => setParams({ limit: l })}
          />
        )}

        {state.showStatusModel && (
          <ConfirmationModel
            modelType="update"
            show
            subTitle={`Update status to ${state.statusToUpdate}`}
            onClose={() =>
              setState((prev) => ({
                ...prev,
                showStatusModel: false,
                selectedUserId: null,
              }))
            }
            onConfirm={() =>
              handleBlockUser(
                state.selectedUserId as string,
                state.statusToUpdate as 'blocked' | 'active',
              )
            }
          />
        )}

        {state.showVerificationModel && (
          <ConfirmationModel
            modelType="update"
            show
            subTitle={`Update status to ${state.statusToUpdate}`}
            onClose={() =>
              setState((prev) => ({
                ...prev,
                showVerificationModel: false,
                selectedUserId: null,
                statusToUpdate: null,
              }))
            }
            onConfirm={() =>
              handleVerifyUser(
                state.selectedUserId as string,
                state.statusToUpdate as 'verified' | 'unVerified',
              )
            }
          />
        )}
      </Card>
    </Layout>
  );
};

export default Customers;
